import React, { useMemo } from 'react';
import {
    Chart as ChartJS,
    CategoryScale,
    LinearScale,
    BarElement,
    Title,
    Tooltip,
    Legend,
    ChartOptions,
} from 'chart.js';
import { Bar } from 'react-chartjs-2';
import { UsageHistory as UsageHistoryType } from 'api/rest/myaliro_billing';
import * as S from './UsageHistory.styled';

// Register Chart.js components
ChartJS.register(CategoryScale, LinearScale, BarElement, Title, Tooltip, Legend);

interface UsageHistoryProps {
    usageHistory: UsageHistoryType[];
}

const UsageHistory: React.FC<UsageHistoryProps> = ({ usageHistory }) => {
    const chartData = useMemo(() => {
        if (!usageHistory || usageHistory.length === 0) {
            return null;
        }

        // Sort history by month (oldest first)
        const sortedHistory = [...usageHistory].sort((a, b) => {
            return new Date(a.month).getTime() - new Date(b.month).getTime();
        });

        // Get month labels
        const labels = sortedHistory.map((item) => {
            const date = new Date(item.month);
            return date.toLocaleDateString('en-US', { month: 'short', year: '2-digit' });
        });

        // Get case counts
        const caseCounts = sortedHistory.map((item) => item.case_count);

        // Determine bar colors based on tier
        const backgroundColors = sortedHistory.map((item) => {
            const tier = item.tier.toLowerCase();
            if (tier.includes('tier 1') || item.fee_per_case === 25) {
                return 'rgba(251, 146, 60, 0.8)'; // Orange for Tier 1
            } else if (tier.includes('tier 2') || item.fee_per_case === 20) {
                return 'rgba(59, 130, 246, 0.8)'; // Blue for Tier 2
            } else {
                return 'rgba(16, 185, 129, 0.8)'; // Green for Tier 3
            }
        });

        const borderColors = sortedHistory.map((item) => {
            const tier = item.tier.toLowerCase();
            if (tier.includes('tier 1') || item.fee_per_case === 25) {
                return 'rgb(251, 146, 60)';
            } else if (tier.includes('tier 2') || item.fee_per_case === 20) {
                return 'rgb(59, 130, 246)';
            } else {
                return 'rgb(16, 185, 129)';
            }
        });

        return {
            labels,
            datasets: [
                {
                    label: 'Cases Submitted',
                    data: caseCounts,
                    backgroundColor: backgroundColors,
                    borderColor: borderColors,
                    borderWidth: 2,
                },
            ],
        };
    }, [usageHistory]);

    const chartOptions: ChartOptions<'bar'> = {
        responsive: true,
        maintainAspectRatio: false,
        plugins: {
            legend: {
                display: false,
            },
            title: {
                display: false,
            },
            tooltip: {
                callbacks: {
                    label: function (context) {
                        const index = context.dataIndex;
                        const historyItem = usageHistory[index];
                        if (!historyItem) return '';

                        return [
                            `Cases: ${historyItem.case_count}`,
                            `Tier: ${historyItem.tier}`,
                            `Fee per case: $${historyItem.fee_per_case}`,
                            `Total fees: $${historyItem.total_case_fees.toFixed(2)}`,
                        ];
                    },
                },
            },
        },
        scales: {
            x: {
                grid: {
                    display: false,
                },
            },
            y: {
                beginAtZero: true,
                ticks: {
                    precision: 0,
                },
                grid: {
                    color: 'rgba(0, 0, 0, 0.05)',
                },
            },
        },
    };

    // Calculate summary statistics
    const summaryStats = useMemo(() => {
        if (!usageHistory || usageHistory.length === 0) {
            return { totalCases: 0, avgCases: 0, highestMonth: null, highestCaseCount: 0 };
        }

        const totalCases = usageHistory.reduce((sum, item) => sum + item.case_count, 0);
        const avgCases = Math.round(totalCases / usageHistory.length);

        const highestItem = usageHistory.reduce(
            (max, item) => (item.case_count > max.case_count ? item : max),
            usageHistory[0]
        );

        const highestMonth = new Date(highestItem.month).toLocaleDateString('en-US', {
            month: 'long',
            year: 'numeric',
        });

        return {
            totalCases,
            avgCases,
            highestMonth,
            highestCaseCount: highestItem.case_count,
        };
    }, [usageHistory]);

    if (!usageHistory || usageHistory.length === 0) {
        return (
            <S.Container>
                <S.SectionTitle>Usage History (Last 12 Months)</S.SectionTitle>
                <S.EmptyState>
                    <S.EmptyStateText>No usage history available yet.</S.EmptyStateText>
                </S.EmptyState>
            </S.Container>
        );
    }

    if (!chartData) {
        return null;
    }

    return (
        <S.Container>
            <S.SectionTitle>Usage History (Last 12 Months)</S.SectionTitle>

            <S.ChartContainer>
                <Bar data={chartData} options={chartOptions} />
            </S.ChartContainer>

            <S.SummarySection>
                <S.SummaryCard>
                    <S.SummaryLabel>Total Cases</S.SummaryLabel>
                    <S.SummaryValue>{summaryStats.totalCases}</S.SummaryValue>
                    <S.SummarySubtext>Last {usageHistory.length} months</S.SummarySubtext>
                </S.SummaryCard>

                <S.SummaryCard>
                    <S.SummaryLabel>Average per Month</S.SummaryLabel>
                    <S.SummaryValue>{summaryStats.avgCases}</S.SummaryValue>
                    <S.SummarySubtext>cases/month</S.SummarySubtext>
                </S.SummaryCard>

                <S.SummaryCard>
                    <S.SummaryLabel>Highest Month</S.SummaryLabel>
                    <S.SummaryValue>{summaryStats.highestCaseCount}</S.SummaryValue>
                    <S.SummarySubtext>{summaryStats.highestMonth}</S.SummarySubtext>
                </S.SummaryCard>
            </S.SummarySection>
        </S.Container>
    );
};

export default UsageHistory;
